import React, { useEffect, useMemo, useState } from 'react';
import EngagerSliderRow from './calc_components/EngagerSliderRow';
import EngagerSelectRow from './calc_components/EngagerSelectRow';
import CalculatorToolsRail from '../components/CalculatorToolsRail';
import CalculatorPrecisionToggle from './calc_components/CalculatorPrecisionToggle';
import { formatYearsCount } from './calc_components/calculatorFormatters';
import { computeEmi } from './EmiCalculator';
import {
  loanFromEmi,
  projectHomeLoanAffordability,
} from './homeLoanAffordabilityMath';
import SurplusWaterChart from './SurplusWaterChart';
import {
  defaultLoanAmount,
  getDefaults,
  maxIncomeGrowth,
  maxExpenseGrowth,
  incomeGrowthStep,
  pctStep,
  minPct,
  minInterestRate,
  maxInterestRate,
  interestRateStep,
  minTenureYears,
  maxTenureYears,
  minLifeEventYear,
  maxLifeEventYearOptions,
  maxMoney,
  moneyStep,
  maxEmi,
  emiStep,
  LIFE_CHANGE_OPTIONS,
  MOVE_IN_OPTIONS,
  LIFE_EVENT_YEAR_OPTIONS,
  POST_MOVE_CHANGE_OPTIONS,
  formatPct,
  formatInterestRatePct,
  snapInterestRate,
  snapPct,
  ModeSwitchLink,
  MoneyField,
  SelectWithInlineAmount,
} from './homeLoanAffordabilityShared';
import './calculators.css';
import './HomeLoanAffordabilityCharts.css';
import './HomeLoanAffordabilityGuided.css';

const STEPS = [
  {
    id: 'income',
    title: 'Tell us about your current household take-home pay',
    tip: 'Your annual take-home pay is the income credited to your bank account after taxes, PF, and other mandatory deductions. It is not your CTC.',
  },
  {
    id: 'expenses',
    title: 'Now tell us about your current annual household expenses',
    tip: 'Include housing, food, transport, existing EMIs, and annualized infrequent spends.',
  },
  {
    id: 'home',
    title: 'Tell us something about the new home you are looking to purchase',
    tip: 'Moving may reduce rent but increase upkeep, society charges, or commute costs.',
  },
  {
    id: 'life',
    title: 'Do you expect any major life events?',
    tip: 'Examples: a child, school fees, supporting parents, or moving from two incomes to one.',
  },
  {
    id: 'loan',
    title: 'Tell us about the loan you are considering',
    tip: 'EMI and loan amount stay linked by rate and tenure—same math as the classic stress-tester.',
  },
  {
    id: 'results',
    title: 'Your surplus outlook',
    tip: 'Adjust tenure, rate, EMI, or loan amount below—the chart updates live.',
  },
];

function ProgressBar({ stepIndex, total }) {
  const pct = ((stepIndex + 1) / total) * 100;
  return (
    <div
      className="hla-guided__progress"
      aria-label={`Step ${stepIndex + 1} of ${total}`}
    >
      <div className="hla-guided__progress-track">
        <div className="hla-guided__progress-fill" style={{ width: `${pct}%` }} />
      </div>
      <p className="hla-guided__progress-label">
        Step {stepIndex + 1} of {total}
      </p>
    </div>
  );
}

export default function HomeLoanAffordabilityGuidedCalculator() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const defaults = getDefaults(false);
  const [stepIndex, setStepIndex] = useState(0);

  const [annualTakeHome, setAnnualTakeHome] = useState(defaults.annualTakeHome);
  const [annualExpenses, setAnnualExpenses] = useState(defaults.annualExpenses);
  const [incomeGrowthPct, setIncomeGrowthPct] = useState(defaults.incomeGrowthPct);
  const [expenseGrowthPct, setExpenseGrowthPct] = useState(defaults.expenseGrowthPct);
  const [moveInYears, setMoveInYears] = useState(defaults.moveInYears);
  const [postMoveExpenseChange, setPostMoveExpenseChange] = useState(
    defaults.postMoveExpenseChange,
  );
  const [postMoveExpenseAmount, setPostMoveExpenseAmount] = useState(
    defaults.postMoveExpenseAmount,
  );
  const [hasLifeEvent, setHasLifeEvent] = useState(defaults.hasLifeEvent);
  const [lifeEventYear, setLifeEventYear] = useState(defaults.lifeEventYear);
  const [lifeEventIncomeChange, setLifeEventIncomeChange] = useState(
    defaults.lifeEventIncomeChange,
  );
  const [lifeEventIncomeAmount, setLifeEventIncomeAmount] = useState(
    defaults.lifeEventIncomeAmount,
  );
  const [lifeEventExpenseChange, setLifeEventExpenseChange] = useState(
    defaults.lifeEventExpenseChange,
  );
  const [lifeEventExpenseAmount, setLifeEventExpenseAmount] = useState(
    defaults.lifeEventExpenseAmount,
  );
  const [tenureYears, setTenureYears] = useState(defaults.tenureYears);
  const [interestRatePct, setInterestRatePct] = useState(defaults.interestRatePct);
  const [loanSizingMode, setLoanSizingMode] = useState(defaults.loanSizingMode);
  const [monthlyEmi, setMonthlyEmi] = useState(defaults.monthlyEmi);
  const [loanAmount, setLoanAmount] = useState(defaults.loanAmount);

  const [takeHomeWords, setTakeHomeWords] = useState(defaults.annualTakeHome);
  const [expensesWords, setExpensesWords] = useState(defaults.annualExpenses);
  const [postMoveWords, setPostMoveWords] = useState(defaults.postMoveExpenseAmount);
  const [lifeIncWords, setLifeIncWords] = useState(defaults.lifeEventIncomeAmount);
  const [lifeExpWords, setLifeExpWords] = useState(defaults.lifeEventExpenseAmount);
  const [emiWords, setEmiWords] = useState(defaults.monthlyEmi);
  const [loanWords, setLoanWords] = useState(defaults.loanAmount);

  const maxLifeYear = Math.max(minLifeEventYear, tenureYears);
  const step = STEPS[stepIndex];
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === STEPS.length - 1;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [stepIndex]);

  useEffect(() => {
    const capped = Math.min(
      maxLifeEventYearOptions,
      Math.max(minLifeEventYear, lifeEventYear),
    );
    if (capped !== lifeEventYear) setLifeEventYear(capped);
  }, [lifeEventYear]);

  function handleStartOver(e) {
    e.preventDefault();
    const d = getDefaults(false);
    setAnnualTakeHome(d.annualTakeHome);
    setAnnualExpenses(d.annualExpenses);
    setIncomeGrowthPct(d.incomeGrowthPct);
    setExpenseGrowthPct(d.expenseGrowthPct);
    setMoveInYears(d.moveInYears);
    setPostMoveExpenseChange(d.postMoveExpenseChange);
    setPostMoveExpenseAmount(d.postMoveExpenseAmount);
    setHasLifeEvent(d.hasLifeEvent);
    setLifeEventYear(d.lifeEventYear);
    setLifeEventIncomeChange(d.lifeEventIncomeChange);
    setLifeEventIncomeAmount(d.lifeEventIncomeAmount);
    setLifeEventExpenseChange(d.lifeEventExpenseChange);
    setLifeEventExpenseAmount(d.lifeEventExpenseAmount);
    setTenureYears(d.tenureYears);
    setInterestRatePct(d.interestRatePct);
    setLoanSizingMode(d.loanSizingMode);
    setMonthlyEmi(d.monthlyEmi);
    setLoanAmount(d.loanAmount);
    setTakeHomeWords(d.annualTakeHome);
    setExpensesWords(d.annualExpenses);
    setPostMoveWords(d.postMoveExpenseAmount);
    setLifeIncWords(d.lifeEventIncomeAmount);
    setLifeExpWords(d.lifeEventExpenseAmount);
    setEmiWords(d.monthlyEmi);
    setLoanWords(d.loanAmount);
    setStepIndex(0);
  }

  const showPostMoveAmount = postMoveExpenseChange !== 'No change';
  const showLifeIncAmount = hasLifeEvent && lifeEventIncomeChange !== 'No change';
  const showLifeExpAmount = hasLifeEvent && lifeEventExpenseChange !== 'No change';

  const sizeByEmi = loanSizingMode === 'emi';
  const resolvedMonthlyEmi = useMemo(() => {
    if (sizeByEmi) return Math.max(0, Number(monthlyEmi) || 0);
    return Math.round(computeEmi(loanAmount, interestRatePct, tenureYears));
  }, [sizeByEmi, monthlyEmi, loanAmount, interestRatePct, tenureYears]);

  const resolvedLoanAmount = useMemo(() => {
    if (!sizeByEmi) return Math.max(0, Number(loanAmount) || 0);
    return Math.round(loanFromEmi(monthlyEmi, interestRatePct, tenureYears));
  }, [sizeByEmi, loanAmount, monthlyEmi, interestRatePct, tenureYears]);

  function handleLoanSizingModeChange(nextMode) {
    if (nextMode === loanSizingMode) return;
    if (nextMode === 'loan') {
      setLoanAmount(defaultLoanAmount);
      setLoanWords(defaultLoanAmount);
    } else {
      const nextEmi = Math.round(
        computeEmi(loanAmount, interestRatePct, tenureYears),
      );
      setMonthlyEmi(nextEmi);
      setEmiWords(nextEmi);
    }
    setLoanSizingMode(nextMode);
  }

  const projection = useMemo(
    () =>
      projectHomeLoanAffordability({
        annualTakeHome,
        annualExpenses,
        incomeGrowthPct,
        expenseGrowthPct,
        moveInYears,
        postMoveExpenseChange,
        postMoveExpenseAmount,
        hasLifeEvent,
        lifeEventYear: Math.min(lifeEventYear, maxLifeYear),
        lifeEventIncomeChange,
        lifeEventIncomeAmount,
        lifeEventExpenseChange,
        lifeEventExpenseAmount,
        monthlyEmi: resolvedMonthlyEmi,
        interestRatePct,
        tenureYears,
        horizonYears: 10,
      }),
    [
      annualTakeHome,
      annualExpenses,
      incomeGrowthPct,
      expenseGrowthPct,
      moveInYears,
      postMoveExpenseChange,
      postMoveExpenseAmount,
      hasLifeEvent,
      lifeEventYear,
      maxLifeYear,
      lifeEventIncomeChange,
      lifeEventIncomeAmount,
      lifeEventExpenseChange,
      lifeEventExpenseAmount,
      resolvedMonthlyEmi,
      interestRatePct,
      tenureYears,
    ],
  );

  function goNext() {
    if (!isLast) setStepIndex((i) => i + 1);
  }

  function goBack() {
    if (!isFirst) setStepIndex((i) => i - 1);
  }

  function renderLoanFields(idPrefix = 'hla-g') {
    return (
      <div className="hla-guided__fields hla-guided__fields--loan">
        <EngagerSliderRow
          id={`${idPrefix}-tenure`}
          label="Loan tenure"
          tooltip="This is the loan tenure used for EMI and for projecting surplus over time."
          value={tenureYears}
          setValue={setTenureYears}
          min={minTenureYears}
          max={maxTenureYears}
          step={1}
          format={formatYearsCount}
          variant="inr"
          isDefaultValue={tenureYears === defaults.tenureYears}
        />

        <EngagerSliderRow
          id={`${idPrefix}-interest`}
          label="Annual interest rate"
          tooltip="Illustrative floating home-loan rate used with EMI and loan amount."
          value={interestRatePct}
          setValue={(v) => setInterestRatePct(snapInterestRate(v))}
          min={minInterestRate}
          max={maxInterestRate}
          step={interestRateStep}
          format={formatInterestRatePct}
          variant="inr"
          isDefaultValue={interestRatePct === defaults.interestRatePct}
        />

        <MoneyField
          id={`${idPrefix}-emi`}
          label={
            sizeByEmi ? (
              'Maximum EMI you are comfortable paying'
            ) : (
              <>
                EMI amount{' '}
                <ModeSwitchLink onClick={() => handleLoanSizingModeChange('emi')}>
                  (enter EMI manually)
                </ModeSwitchLink>
              </>
            )
          }
          labelAria={
            sizeByEmi
              ? 'Maximum EMI you are comfortable paying'
              : 'EMI amount (enter EMI manually)'
          }
          tooltip={
            sizeByEmi
              ? 'See how your EMI could change your annual surplus over the loan tenure.'
              : 'Monthly EMI implied by the loan amount, interest rate, and tenure.'
          }
          value={sizeByEmi ? monthlyEmi : resolvedMonthlyEmi}
          setValue={setMonthlyEmi}
          wordsValue={sizeByEmi ? emiWords : resolvedMonthlyEmi}
          setWordsValue={setEmiWords}
          max={maxEmi}
          step={emiStep}
          disabled={!sizeByEmi}
          highlightComputed={!sizeByEmi}
          isDefaultValue={sizeByEmi && monthlyEmi === defaults.monthlyEmi}
        />

        <MoneyField
          id={`${idPrefix}-loan-amount`}
          label={
            sizeByEmi ? (
              <>
                Loan amount{' '}
                <ModeSwitchLink onClick={() => handleLoanSizingModeChange('loan')}>
                  (enter loan amount manually)
                </ModeSwitchLink>
              </>
            ) : (
              'Loan amount'
            )
          }
          labelAria={
            sizeByEmi
              ? 'Loan amount (enter loan amount manually)'
              : 'Loan amount'
          }
          tooltip={
            sizeByEmi
              ? 'Loan principal supported by your max EMI, rate, and tenure.'
              : 'Enter the home-loan principal you are considering.'
          }
          value={sizeByEmi ? resolvedLoanAmount : loanAmount}
          setValue={setLoanAmount}
          wordsValue={sizeByEmi ? resolvedLoanAmount : loanWords}
          setWordsValue={setLoanWords}
          max={maxMoney}
          step={moneyStep}
          disabled={sizeByEmi}
          highlightComputed={sizeByEmi}
          isDefaultValue={!sizeByEmi && loanAmount === defaults.loanAmount}
        />
      </div>
    );
  }

  let stepBody = null;

  if (step.id === 'income') {
    stepBody = (
      <div className="hla-guided__fields">
        <MoneyField
          id="hla-g-take-home"
          label="Annual take-home pay"
          value={annualTakeHome}
          setValue={setAnnualTakeHome}
          wordsValue={takeHomeWords}
          setWordsValue={setTakeHomeWords}
          isDefaultValue={annualTakeHome === defaults.annualTakeHome}
        />
        <EngagerSliderRow
          id="hla-g-income-growth"
          label="Estimated growth rate of your take-home pay"
          tooltip="Expected yearly growth in take-home pay. Use 0% to stress-test salary stagnation."
          value={incomeGrowthPct}
          setValue={(v) =>
            setIncomeGrowthPct(snapPct(v, maxIncomeGrowth, incomeGrowthStep))
          }
          min={minPct}
          max={maxIncomeGrowth}
          step={incomeGrowthStep}
          format={formatPct}
          variant="inr"
          isDefaultValue={incomeGrowthPct === defaults.incomeGrowthPct}
        />
      </div>
    );
  } else if (step.id === 'expenses') {
    stepBody = (
      <div className="hla-guided__fields">
        <MoneyField
          id="hla-g-expenses"
          label="Current annual household expenses"
          value={annualExpenses}
          setValue={setAnnualExpenses}
          wordsValue={expensesWords}
          setWordsValue={setExpensesWords}
          isDefaultValue={annualExpenses === defaults.annualExpenses}
        />
        <EngagerSliderRow
          id="hla-g-expense-growth"
          label="Estimated annual growth rate of your expenses"
          tooltip="Expected yearly rise in household expenses (inflation and lifestyle)."
          value={expenseGrowthPct}
          setValue={(v) => setExpenseGrowthPct(snapPct(v, maxExpenseGrowth))}
          min={minPct}
          max={maxExpenseGrowth}
          step={pctStep}
          format={formatPct}
          variant="inr"
          isDefaultValue={expenseGrowthPct === defaults.expenseGrowthPct}
        />
      </div>
    );
  } else if (step.id === 'home') {
    stepBody = (
      <div className="hla-guided__fields hla-guided__fields--stack">
        <EngagerSelectRow
          id="hla-g-move-in"
          label="When do you expect to move into your new home?"
          tooltip="Enter your best estimate of when you expect to move into the new house."
          value={String(moveInYears)}
          onChange={(e) => setMoveInYears(Number(e.target.value))}
          isDefaultValue={moveInYears === defaults.moveInYears}
        >
          {MOVE_IN_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </EngagerSelectRow>

        <SelectWithInlineAmount
          id="hla-g-post-move-change"
          label="Do you expect your annual household expenses to change after moving into your new home?"
          tooltip="Net change after moving—for example lower rent replaced by higher upkeep, commute, or society charges."
          value={postMoveExpenseChange}
          onChange={setPostMoveExpenseChange}
          options={POST_MOVE_CHANGE_OPTIONS}
          showAmount={showPostMoveAmount}
          amount={postMoveExpenseAmount}
          setAmount={setPostMoveExpenseAmount}
          amountWords={postMoveWords}
          setAmountWords={setPostMoveWords}
          isDefaultSelect={
            postMoveExpenseChange === defaults.postMoveExpenseChange
          }
          isDefaultAmount={
            postMoveExpenseAmount === defaults.postMoveExpenseAmount
          }
        />
      </div>
    );
  } else if (step.id === 'life') {
    stepBody = (
      <div className="hla-guided__fields hla-guided__fields--stack">
        <EngagerSelectRow
          id="hla-g-life-event"
          label="Do you expect any major life events in the future?"
          value={hasLifeEvent ? 'Yes' : 'No'}
          onChange={(e) => {
            const next = e.target.value === 'Yes';
            setHasLifeEvent(next);
            if (next) {
              setLifeEventYear(defaults.lifeEventYear);
              setLifeEventExpenseChange(defaults.lifeEventExpenseChange);
              setLifeEventExpenseAmount(defaults.lifeEventExpenseAmount);
              setLifeExpWords(defaults.lifeEventExpenseAmount);
            }
          }}
          isDefaultValue={hasLifeEvent === defaults.hasLifeEvent}
        >
          <option value="No">No</option>
          <option value="Yes">Yes</option>
        </EngagerSelectRow>

        {hasLifeEvent ? (
          <>
            <EngagerSelectRow
              id="hla-g-life-year"
              label="When do you expect this life event to occur?"
              tooltip="Enter your best estimate in years from now."
              value={String(
                Math.min(
                  maxLifeEventYearOptions,
                  Math.max(minLifeEventYear, lifeEventYear),
                ),
              )}
              onChange={(e) => setLifeEventYear(Number(e.target.value))}
              isDefaultValue={lifeEventYear === defaults.lifeEventYear}
            >
              {LIFE_EVENT_YEAR_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </EngagerSelectRow>

            <SelectWithInlineAmount
              id="hla-g-life-expense-change"
              label="Do you expect this life event to change your annual household expenses?"
              tooltip="For example school fees, childcare, or elder-care costs."
              value={lifeEventExpenseChange}
              onChange={setLifeEventExpenseChange}
              options={LIFE_CHANGE_OPTIONS}
              showAmount={showLifeExpAmount}
              amount={lifeEventExpenseAmount}
              setAmount={setLifeEventExpenseAmount}
              amountWords={lifeExpWords}
              setAmountWords={setLifeExpWords}
              isDefaultSelect={
                lifeEventExpenseChange === defaults.lifeEventExpenseChange
              }
              isDefaultAmount={
                lifeEventExpenseAmount === defaults.lifeEventExpenseAmount
              }
            />

            <SelectWithInlineAmount
              id="hla-g-life-income-change"
              label="Do you expect this life event to change your annual household income?"
              tooltip="For example a partner pausing work (decrease) or a promotion (increase)."
              value={lifeEventIncomeChange}
              onChange={setLifeEventIncomeChange}
              options={LIFE_CHANGE_OPTIONS}
              showAmount={showLifeIncAmount}
              amount={lifeEventIncomeAmount}
              setAmount={setLifeEventIncomeAmount}
              amountWords={lifeIncWords}
              setAmountWords={setLifeIncWords}
              isDefaultSelect={
                lifeEventIncomeChange === defaults.lifeEventIncomeChange
              }
              isDefaultAmount={
                lifeEventIncomeAmount === defaults.lifeEventIncomeAmount
              }
            />
          </>
        ) : null}
      </div>
    );
  } else if (step.id === 'loan') {
    stepBody = renderLoanFields('hla-g');
  } else if (step.id === 'results') {
    stepBody = (
      <div className="hla-guided__results">
        {renderLoanFields('hla-g-results')}
        <div className="hla-chart-panel" aria-label="Expenses and surplus chart">
          <CalculatorPrecisionToggle className="calc-precision-toggle--inline" />
          <SurplusWaterChart
            years={projection.years}
            title="Surplus income & expenses"
          />
        </div>
        <p className="hla-guided__footnote">
          Illustrative cash-flow stress test. Excludes fees, taxes, rate resets, and
          lender eligibility rules—not advice.
        </p>
      </div>
    );
  }

  const calculatorCard = (
    <div className="appContainer nps-ups--compact home-loan-affordability home-loan-affordability--guided">
      <p className="hla-guided__eyebrow">Home Loan Affordability</p>
      <ProgressBar stepIndex={stepIndex} total={STEPS.length} />

      <header className="hla-guided__header">
        <h1 className="hla-guided__title">{step.title}</h1>
        {step.tip ? <p className="hla-guided__tip">{step.tip}</p> : null}
      </header>

      <div className="hla-guided__body" key={step.id}>
        {stepBody}
      </div>

      <div className="hla-guided__nav">
        <button
          type="button"
          className="hla-guided__btn hla-guided__btn--ghost"
          onClick={goBack}
          disabled={isFirst}
        >
          Back
        </button>
        <div className="hla-guided__nav-right">
          {isLast ? (
            <button
              type="button"
              className="hla-guided__btn hla-guided__btn--ghost"
              onClick={handleStartOver}
            >
              Start over
            </button>
          ) : null}
          {!isLast ? (
            <button
              type="button"
              className="hla-guided__btn hla-guided__btn--primary"
              onClick={goNext}
            >
              Continue
            </button>
          ) : (
            <button
              type="button"
              className="hla-guided__btn hla-guided__btn--primary"
              onClick={() => setStepIndex(0)}
            >
              Review answers
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="calculator-page-layout calculator-page-layout--hla-guided">
      <div className="hla-guided-shell">{calculatorCard}</div>
      <div className="calculator-rail-shell">
        <CalculatorToolsRail currentToolKey="home-loan-affordability-guided" />
      </div>
    </div>
  );
}
